import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Tooltip from './Tooltip';

/** Standard / Skill / Objective — subtle accent colors for taxonomy UI */
const TAX_TYPE_ACCENTS = {
  Standard: {
    stroke: '#3b82f6',
    selBg: '#eff6ff',
    selBorder: '#bfdbfe',
    header: '#1d4ed8',
  },
  Skill: {
    stroke: '#10b981',
    selBg: '#ecfdf5',
    selBorder: '#a7f3d0',
    header: '#047857',
  },
  Objective: {
    stroke: '#f59e0b',
    selBg: '#fffbeb',
    selBorder: '#fde68a',
    header: '#b45309',
  },
};

const typeRowStyle = (kind, selected) => {
  const a = TAX_TYPE_ACCENTS[kind] || TAX_TYPE_ACCENTS.Standard;
  return {
    backgroundColor: selected ? a.selBg : 'rgba(255,255,255,0.65)',
    border: selected ? `1px solid ${a.selBorder}` : '1px solid #e2e8f0',
    borderLeft: `3px solid ${a.stroke}`,
  };
};

/** Shared overlay + surfaces for taxonomy modals (compact, low-chroma) */
const TX = {
  overlay: (zIndex) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex,
  }),
  panel: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 24px rgba(15, 23, 42, 0.07)',
  },
  title: { margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.02em' },
  hint: { fontSize: '12px', color: '#64748b', lineHeight: 1.45 },
  label: { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#64748b' },
  field: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    backgroundColor: '#fafafa',
    boxSizing: 'border-box',
  },
  btnSecondary: {
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: 500,
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#334155',
    cursor: 'pointer',
  },
  btnPrimary: {
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: 500,
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#334155',
    color: '#ffffff',
    cursor: 'pointer',
  },
  btnPrimaryDisabled: { backgroundColor: '#cbd5e1', color: '#f8fafc', cursor: 'not-allowed' },
  foot: { borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '14px' },
  typeAccents: TAX_TYPE_ACCENTS,
  sel: (on) => ({
    backgroundColor: on ? '#f1f5f9' : 'transparent',
    border: on ? '1px solid #cbd5e1' : '1px solid transparent',
  }),
};

/** `concentrations` is a list of pathway roots: CTE concentrations and/or Course-Subject rows (see mergePathwayRoots). */
const TaxonomySelector = ({ 
  selectedTaxonomy, 
  onTaxonomyChange, 
  concentrations = [], 
  disabled = false,
  primaryConcentration = null,
  onSelectedConcentrationsChange = null,
  selectedConcentrations: initialSelectedConcentrations = [],
  taxonomyItems: externalTaxonomyItems = [],
  isStudentRole = false,
  showPrimaryBehavior = true
}) => {
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedConcentrations, setSelectedConcentrations] = useState(initialSelectedConcentrations);
  const [concentrationTaxonomy, setConcentrationTaxonomy] = useState({});
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    concentrationId: null,
    taxonomyType: '',
    title: '',
    selectedItems: [],
    onSelectionChange: null
  });

  const [parentSelectionModal, setParentSelectionModal] = useState({
    isOpen: false,
    item: null,
    parents: [],
    onParentSelected: null,
    concentrationId: null,
    selectedTaxonomy: null
  });

  // Create modal state
  const [createModal, setCreateModal] = useState({
    isOpen: false,
    concentrationId: null,
    onItemCreated: null
  });

  // Fetch all taxonomy items on component mount (only if not provided externally)
  const fetchTaxonomyItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching taxonomy items...');
      const response = await axios.get('/api/taxonomy/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Taxonomy response:', response.data);
      setTaxonomyItems(response.data.items || []);
      console.log('Set taxonomy items:', response.data.items || []);
    } catch (err) {
      console.error('Error fetching taxonomy items:', err);
      setError('Failed to load taxonomy items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (externalTaxonomyItems && externalTaxonomyItems.length > 0) {
      // Use external taxonomy items if provided
      setTaxonomyItems(externalTaxonomyItems);
      setLoading(false);
    } else {
      // Fetch taxonomy items if not provided externally
      fetchTaxonomyItems();
    }
  }, [externalTaxonomyItems]);

  // Handle changes to initialSelectedConcentrations prop
  useEffect(() => {
    if (initialSelectedConcentrations && initialSelectedConcentrations.length > 0) {
      console.log('🔍 TaxonomySelector - initialSelectedConcentrations prop changed:', initialSelectedConcentrations);
      setSelectedConcentrations(initialSelectedConcentrations);
    }
  }, [initialSelectedConcentrations]);

  // Initialize selected concentrations and taxonomy from props
  useEffect(() => {
    console.log('🔍 TaxonomySelector useEffect triggered with:', {
      initialSelectedConcentrations,
      selectedTaxonomy: selectedTaxonomy?.length || 0,
      taxonomyItems: taxonomyItems?.length || 0,
      selectedConcentrations: selectedConcentrations?.length || 0
    });
    
    // If we have initial selected concentrations from props, use those
    if (initialSelectedConcentrations && initialSelectedConcentrations.length > 0) {
      console.log('TaxonomySelector - using initial selectedConcentrations from props:', initialSelectedConcentrations);
      setSelectedConcentrations(initialSelectedConcentrations);
      
      // Also initialize concentrationTaxonomy from selectedTaxonomy if available
      if (selectedTaxonomy && selectedTaxonomy.length > 0 && taxonomyItems.length > 0) {
        const grouped = {};
        selectedTaxonomy.forEach(item => {
          if (!grouped[item.concentration]) {
            grouped[item.concentration] = { standards: [], skills: [], objectives: [] };
          }
          
          // Find the full taxonomy item object
          const fullTaxonomyItem = taxonomyItems.find(t => t.id === item.taxonomy_id);
          if (fullTaxonomyItem) {
            const typeKey = fullTaxonomyItem.type.toLowerCase() + 's';
            if (grouped[item.concentration][typeKey]) {
              grouped[item.concentration][typeKey].push(fullTaxonomyItem);
            }
          }
        });
        console.log('TaxonomySelector - initializing concentrationTaxonomy from selectedTaxonomy:', grouped);
        setConcentrationTaxonomy(grouped);
      } else {
        console.log('TaxonomySelector - cannot initialize concentrationTaxonomy:', {
          hasSelectedTaxonomy: !!selectedTaxonomy,
          selectedTaxonomyLength: selectedTaxonomy?.length || 0,
          hasTaxonomyItems: !!taxonomyItems,
          taxonomyItemsLength: taxonomyItems?.length || 0
        });
      }
    } else if (selectedTaxonomy && selectedTaxonomy.length > 0) {
      // Extract unique concentrations from selected taxonomy
      const uniqueConcentrations = [...new Set(selectedTaxonomy.map(item => item.concentration))];
      
      console.log('TaxonomySelector - selectedTaxonomy:', selectedTaxonomy);
      console.log('TaxonomySelector - uniqueConcentrations:', uniqueConcentrations);
      console.log('TaxonomySelector - current selectedConcentrations:', selectedConcentrations);
      
      // Update selectedConcentrations if we have taxonomy data but no selected concentrations
      // This handles the case when loading existing assignment data
      if (selectedConcentrations.length === 0 || 
          (uniqueConcentrations.length > 0 && !uniqueConcentrations.every(conc => selectedConcentrations.includes(conc)))) {
        console.log('TaxonomySelector - updating selectedConcentrations to:', uniqueConcentrations);
        setSelectedConcentrations(uniqueConcentrations);
        
        // Notify parent of concentration change
        if (onSelectedConcentrationsChange) {
          onSelectedConcentrationsChange(uniqueConcentrations);
        }
      }
      
      // Group taxonomy items by concentration and map to full taxonomy objects
      const grouped = {};
      selectedTaxonomy.forEach(item => {
        if (!grouped[item.concentration]) {
          grouped[item.concentration] = { standards: [], skills: [], objectives: [] };
        }
        
        // Find the full taxonomy item object
        const fullTaxonomyItem = taxonomyItems.find(t => t.id === item.taxonomy_id);
        if (fullTaxonomyItem) {
          const typeKey = fullTaxonomyItem.type.toLowerCase() + 's';
          if (grouped[item.concentration][typeKey]) {
            grouped[item.concentration][typeKey].push(fullTaxonomyItem);
          }
        }
      });
      setConcentrationTaxonomy(grouped);
    }
  }, [selectedTaxonomy, taxonomyItems, selectedConcentrations, onSelectedConcentrationsChange, initialSelectedConcentrations]);



  // Auto-select primary concentration when it changes
  useEffect(() => {
    if (primaryConcentration && !selectedConcentrations.includes(primaryConcentration)) {
      const newSelectedConcentrations = [...selectedConcentrations, primaryConcentration];
      setSelectedConcentrations(newSelectedConcentrations);
      setConcentrationTaxonomy(prev => ({
        ...prev,
        [primaryConcentration]: { standards: [], skills: [], objectives: [] }
      }));
      
      // Notify parent of concentration change
      if (onSelectedConcentrationsChange) {
        onSelectedConcentrationsChange(newSelectedConcentrations);
      }
    }
  }, [primaryConcentration, selectedConcentrations, onSelectedConcentrationsChange]);

  const openModal = (concentrationId, taxonomyType) => {
    // Get the current taxonomy items for this concentration
    const concentrationData = concentrationTaxonomy[concentrationId] || {};
    const currentSelection = [
      ...((concentrationData.standards || []).map(s => s.id)),
      ...((concentrationData.skills || []).map(s => s.id)),
      ...((concentrationData.objectives || []).map(s => s.id))
    ];
    
    console.log('openModal - current selection:', currentSelection);
    
    setModalConfig({
      isOpen: true,
      concentrationId,
      taxonomyType,
      title: `Select skills, standards, or objectives for ${concentrations.find(c => c.id === concentrationId)?.title || 'pathway'}`,
      selectedItems: currentSelection,
      onSelectionChange: (selectedIds, fullItems, taxonomyArray) => {
        // Update the entire concentration's taxonomy data (like RequestFEHoursByExternalActivity.js)
        const safeFullItems = Array.isArray(fullItems) ? fullItems : [];
        const standards = safeFullItems.filter(i => i.type === 'Standard');
        const skills = safeFullItems.filter(i => i.type === 'Skill');
        const objectives = safeFullItems.filter(i => i.type === 'Objective');
        
        // Update the concentration taxonomy state
        setConcentrationTaxonomy(prev => ({
          ...prev,
          [concentrationId]: {
            standards,
            skills,
            objectives
          }
        }));
        
        // Use the taxonomyArray from the modal if provided, otherwise create it
        const newTaxonomyItems = taxonomyArray || safeFullItems.map(item => {
          let parentId = null;
          
          // For skills, find the parent standard if available
          if (item.type === 'Skill') {
            const parentStandard = taxonomyItems.find(p => 
              p.type === 'Standard' && 
              p.concentrations && p.concentrations.includes(concentrationId) &&
              item.parents && item.parents.includes(p.id)
            );
            if (parentStandard) {
              parentId = parentStandard.id;
            }
          }
          
          return {
            taxonomy_id: item.id,
            parent_id: parentId,
            concentration: concentrationId
          };
        });
        
        // Merge new taxonomy items with existing ones, removing old items for this concentration
        const existingTaxonomyForOtherConcentrations = selectedTaxonomy.filter(t => t.concentration !== concentrationId);
        const updatedTaxonomy = [...existingTaxonomyForOtherConcentrations, ...newTaxonomyItems];
        
        // Update the main taxonomy array
        onTaxonomyChange(updatedTaxonomy);
      }
    });
  };

  const closeModal = () => {
    setModalConfig({
      isOpen: false,
      concentrationId: null,
      taxonomyType: '',
      title: '',
      selectedItems: [],
      onSelectionChange: null
    });
  };

  const openParentSelectionModal = (item, parents, onParentSelected) => {
    setParentSelectionModal({
      isOpen: true,
      item,
      parents,
      onParentSelected,
      concentrationId: modalConfig.concentrationId,
      selectedTaxonomy
    });
  };

  const closeParentSelectionModal = () => {
    setParentSelectionModal({
      isOpen: false,
      item: null,
      parents: [],
      onParentSelected: null,
      concentrationId: null,
      selectedTaxonomy: null
    });
  };

  const openCreateModal = (concentrationId, onItemCreated) => {
    setCreateModal({
      isOpen: true,
      concentrationId,
      onItemCreated
    });
  };

  const closeCreateModal = () => {
    setCreateModal({
      isOpen: false,
      concentrationId: null,
      onItemCreated: null
    });
  };

  const getTooltipContent = (item) => {
    let content = '';
    if (item.title) {
      content += `Title: ${item.title}`;
    }
    if (item.description) {
      if (content) content += '\n\n';
      content += `Description: ${item.description}`;
    }
    if (!content) {
      content = 'No title or description available';
    }
    return content;
  };

  const getDisplayName = (item) => {
    if (item.title) return item.title;
    if (item.description) {
      return item.description.length > 50 
        ? `${item.description.substring(0, 50)}...` 
        : item.description;
    }
    return `Untitled ${item.type} (${item.id.substring(0, 8)}...)`;
  };

  // Concentration toggle handler
  const handleConcentrationToggle = (concentrationId) => {
    if (disabled) return;
    // Prevent deselecting primary concentration only if showPrimaryBehavior is true
    if (showPrimaryBehavior && concentrationId === primaryConcentration) return;
    const isSelected = selectedConcentrations.includes(concentrationId);
    let newSelectedConcentrations;
    if (isSelected) {
      // Deselect: remove from selectedConcentrations
      newSelectedConcentrations = selectedConcentrations.filter(id => id !== concentrationId);
      setSelectedConcentrations(newSelectedConcentrations);
      
      // Remove all taxonomy items for this concentration
      const filteredTaxonomy = selectedTaxonomy.filter(t => t.concentration !== concentrationId);
      setConcentrationTaxonomy(prev => {
        const newMap = { ...prev };
        delete newMap[concentrationId];
        return newMap;
      });
      
      // IMPORTANT: Call onSelectedConcentrationsChange FIRST to update otherConcentrations
      // Then call onTaxonomyChange to update the taxonomy
      if (onSelectedConcentrationsChange) {
        console.log('🔄 Deselecting concentration, calling onSelectedConcentrationsChange with:', newSelectedConcentrations);
        onSelectedConcentrationsChange(newSelectedConcentrations);
      }
      
      // Update the taxonomy to remove the deselected concentration
      onTaxonomyChange(filteredTaxonomy);
    } else {
      // Select: add to selectedConcentrations
      newSelectedConcentrations = [...selectedConcentrations, concentrationId];
      setSelectedConcentrations(newSelectedConcentrations);
      if (onSelectedConcentrationsChange) onSelectedConcentrationsChange(newSelectedConcentrations);
      // Note: We don't call onTaxonomyChange here because the user hasn't selected any taxonomy items yet
      // The taxonomy will be updated when they actually select skills/standards/objectives
    }
  };

  const removeTaxonomyItem = (concentrationId, itemId) => {
    const newTaxonomy = selectedTaxonomy.filter(item => 
      !(item.concentration === concentrationId && item.taxonomy_id === itemId)
    );
    onTaxonomyChange(newTaxonomy);
    
    // Update concentration taxonomy state
    setConcentrationTaxonomy(prev => {
      const newState = { ...prev };
      if (newState[concentrationId]) {
        newState[concentrationId] = {
          ...newState[concentrationId],
          standards: newState[concentrationId].standards.filter(item => item.id !== itemId),
          skills: newState[concentrationId].skills.filter(item => item.id !== itemId),
          objectives: newState[concentrationId].objectives.filter(item => item.id !== itemId)
        };
      }
      return newState;
    });
  };

  if (loading) {
    return <div>Loading taxonomy items...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  return (
    <div>
      {/* Pathway roots (CTE concentrations and/or course subjects) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: '1rem' }}>
        {concentrations.map(concentration => {
          const isChecked = selectedConcentrations.includes(concentration.id);
          const concentrationData = concentrationTaxonomy[concentration.id] || {};
          const allTags = [
            ...(concentrationData.standards || []),
            ...(concentrationData.skills || []),
            ...(concentrationData.objectives || [])
          ];
          
          return (
            <label key={concentration.id} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              minWidth: 240,
              background: isChecked ? 'rgba(29, 78, 137, 0.08)' : '#ffffff',
              borderRadius: '16px',
              padding: '16px',
              border: isChecked ? '2px solid #1D4E89' : '2px solid #e5e7eb',
              fontWeight: 500,
              boxShadow: isChecked ? '0 4px 12px rgba(29, 78, 137, 0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
              flexDirection: 'column',
              position: 'relative',
              marginBottom: 8,
              transition: 'all 0.2s ease',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1
            }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => !disabled && handleConcentrationToggle(concentration.id)}
                  style={{ accentColor: '#1D4E89', width: 20, height: 20, marginRight: 12 }}
                  disabled={disabled || (showPrimaryBehavior && concentration.id === primaryConcentration)}
                />
                <span style={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>
                  {concentration.title}
                  {concentration.pathwayKind === 'course_subject' && (
                    <span style={{ fontSize: '11px', color: '#64748b', marginLeft: 8, fontWeight: 500 }}>
                      Subject
                    </span>
                  )}
                  {showPrimaryBehavior && concentration.id === primaryConcentration && (
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#1D4E89', 
                      marginLeft: '8px',
                      fontWeight: 'normal'
                    }}>
                      (Primary)
                    </span>
                  )}
                </span>
                {isChecked && !disabled && (
                  <button
                    type="button"
                    onClick={() => openModal(concentration.id, 'Standard')}
                    style={{ 
                      marginLeft: 'auto', 
                      background: '#1D4E89', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      padding: '6px 16px', 
                      fontSize: '13px', 
                      cursor: 'pointer', 
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Add Skills/Standards
                  </button>
                )}
              </div>
              {isChecked && allTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, width: '100%' }}>
                  {allTags.map((item, idx) => (
                    <Tooltip key={item.id || idx} content={getTooltipContent(item)}>
                      <span
                        style={{
                          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          color: '#1e40af',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          border: '1px solid #93c5fd',
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          fontWeight: 500,
                          position: 'relative'
                        }}
                      >
                        {getDisplayName(item)}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              )}
            </label>
          );
        })}
      </div>

      {/* Taxonomy Selection Modal - Using same structure as ConcentrationTaxonomyModal */}
      {modalConfig.isOpen && (
        <TaxonomySelectionModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          concentrationId={modalConfig.concentrationId}
          selectedItems={modalConfig.selectedItems}
          onSelectionChange={modalConfig.onSelectionChange}
          title={modalConfig.title}
          pathwayDisplayName={
            concentrations.find((c) => c.id === modalConfig.concentrationId)?.title || 'this pathway'
          }
          initialViewType={modalConfig.taxonomyType || 'Standard'}
          taxonomyItems={taxonomyItems}
          loading={loading} // Pass loading state to modal
          selectedTaxonomy={selectedTaxonomy} // Pass selectedTaxonomy to modal
          onParentSelection={openParentSelectionModal} // Pass parent selection handler
          onOpenCreateModal={openCreateModal} // Pass create modal handler
          onFetchTaxonomyItems={fetchTaxonomyItems} // Pass fetch function
          isStudentRole={isStudentRole} // Pass student role to modal
        />
      )}

      {/* Parent Selection Modal */}
      <ParentSelectionModal
        isOpen={parentSelectionModal.isOpen}
        onClose={closeParentSelectionModal}
        item={parentSelectionModal.item}
        parents={parentSelectionModal.parents}
        onParentSelected={parentSelectionModal.onParentSelected}
        concentrationId={parentSelectionModal.concentrationId}
        selectedTaxonomy={parentSelectionModal.selectedTaxonomy}
      />

      {/* Create Taxonomy Modal */}
              <CreateTaxonomyModal
          isOpen={createModal.isOpen}
          onClose={closeCreateModal}
          concentrationId={createModal.concentrationId}
          onItemCreated={createModal.onItemCreated}
          concentrations={concentrations}
          taxonomyItems={taxonomyItems}
        />
    </div>
  );
};

// Separate modal component that matches ConcentrationTaxonomyModal structure with nested display
const TaxonomySelectionModal = ({ 
  isOpen, 
  onClose, 
  concentrationId, 
  selectedItems, 
  onSelectionChange,
  title,
  pathwayDisplayName = 'this pathway',
  initialViewType = 'Standard',
  taxonomyItems,
  loading,
  selectedTaxonomy,
  onParentSelection,
  onOpenCreateModal,
  onFetchTaxonomyItems,
  isStudentRole = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [taxonomyType, setTaxonomyType] = useState(initialViewType);
  const [allSelectedItems, setAllSelectedItems] = useState({ Standard: [], Skill: [], Objective: [] });
  const [expandedStandards, setExpandedStandards] = useState(new Set());
  const [selectedParentChildRelationships, setSelectedParentChildRelationships] = useState({}); // Track parent-child relationships: { skillId: [parentId1, parentId2, ...] }

  useEffect(() => {
    if (isOpen) {
      setTaxonomyType(initialViewType || 'Standard');
    }
  }, [isOpen, initialViewType]);

  useEffect(() => {
    if (isOpen && Array.isArray(taxonomyItems) && taxonomyItems.length > 0) {
      const byType = { Standard: [], Skill: [], Objective: [] };
      const parentChildMap = {};
      
      // Initialize from the selectedItems prop (which contains existing taxonomy IDs)
      if (Array.isArray(selectedItems)) {
        selectedItems.forEach(id => {
          const found = taxonomyItems.find(i => i.id === id);
          if (found && byType[found.type]) {
            byType[found.type].push(id);
          }
        });
      }
      
      // Initialize parent-child relationships from selectedTaxonomy
      if (selectedTaxonomy && Array.isArray(selectedTaxonomy)) {
        selectedTaxonomy.forEach(item => {
          if (item.parent_id && item.taxonomy_id) {
            if (!parentChildMap[item.taxonomy_id]) {
              parentChildMap[item.taxonomy_id] = [];
            }
            parentChildMap[item.taxonomy_id].push(item.parent_id);
          }
        });
      }
      
      console.log('Modal initialization - byType:', byType);
      console.log('Parent-child relationships:', parentChildMap);
      setAllSelectedItems(byType);
      setSelectedParentChildRelationships(parentChildMap);
      
      // Auto-expand standards that have selected skills
      const standardsToExpand = new Set();
      byType.Skill.forEach(skillId => {
        const skill = taxonomyItems.find(i => i.id === skillId);
        if (skill && skill.parents) {
          skill.parents.forEach(parentId => {
            const parent = taxonomyItems.find(i => i.id === parentId);
            if (parent && parent.type === 'Standard' && parent.concentrations.includes(concentrationId)) {
              standardsToExpand.add(parentId);
            }
          });
        }
      });
      setExpandedStandards(standardsToExpand);
    }
  }, [isOpen, taxonomyItems, selectedItems, concentrationId, selectedTaxonomy]);

  const handleItemToggle = (itemId, itemType = null) => {
    // Use the provided itemType or fall back to taxonomyType
    const currentType = itemType || taxonomyType;
    const prevTypeItems = allSelectedItems[currentType] || [];
    const isCurrentlySelected = prevTypeItems.includes(itemId);
    
    let newSelection;
    if (isCurrentlySelected) {
      // Deselecting the item
      newSelection = prevTypeItems.filter(id => id !== itemId);
      
      // Note: We don't remove parent-child relationships here anymore
      // That logic is handled in handleItemClick for skills with multiple parents
    } else {
      // Selecting the item
      newSelection = [...prevTypeItems, itemId];
      
      // If this is a standard being selected, auto-expand it but don't auto-select children
      if (currentType === 'Standard') {
        // Auto-expand the standard
        setExpandedStandards(prev => new Set([...prev, itemId]));
      }
    }
    
    return {
      ...allSelectedItems,
      [currentType]: newSelection
    };
  };

  const handleItemClick = (item, parentStandardId = null) => {
    // Check if item has multiple parents
    if (item.parents && item.parents.length > 1) {
      // Find the parent items that belong to this concentration
      const availableParents = taxonomyItems.filter(parent => 
        item.parents.includes(parent.id) && 
        parent.concentrations && 
        parent.concentrations.includes(concentrationId)
      );
      
      if (availableParents.length > 1) {
        // If we have a specific parent standard ID (from nested selection), use it
        if (parentStandardId) {
          // Check if this skill is already selected under this specific parent
          const isAlreadySelectedUnderThisParent = allSelectedItems.Skill.includes(item.id) && 
            selectedParentChildRelationships[item.id] && selectedParentChildRelationships[item.id].includes(parentStandardId);
          
          if (isAlreadySelectedUnderThisParent) {
            // Deselect from this specific parent only
            setSelectedParentChildRelationships(prev => {
              const newMap = { ...prev };
              newMap[item.id] = newMap[item.id].filter(id => id !== parentStandardId);
              // If no more parents for this skill, remove it from selected items
              if (newMap[item.id].length === 0) {
                delete newMap[item.id];
                const newState = handleItemToggle(item.id, item.type);
                setAllSelectedItems(newState);
              }
              return newMap;
            });
          } else {
            // Select under this specific parent (allow multiple parents for same skill)
            setSelectedParentChildRelationships(prev => {
              const newMap = { ...prev };
              newMap[item.id] = [...(newMap[item.id] || []), parentStandardId];
              return newMap;
            });
            // Only add to selected items if not already selected
            if (!allSelectedItems.Skill.includes(item.id)) {
              const newState = handleItemToggle(item.id, item.type);
              setAllSelectedItems(newState);
            }
          }
          return;
        }
        
        // Show parent selection modal only for Skills view or when parent is unclear
        onParentSelection(item, availableParents, (selectedParentIds) => {
          console.log('Parent selection callback - received selectedParentIds:', selectedParentIds);
          // Handle multiple parent selection
          if (Array.isArray(selectedParentIds)) {
            // Check if this skill is already selected
            const isAlreadySelected = allSelectedItems.Skill.includes(item.id);
            
            if (isAlreadySelected && selectedParentIds.length === 0) {
              // Deselect the skill if no parents are selected
              setSelectedParentChildRelationships(prev => {
                const newMap = { ...prev };
                delete newMap[item.id];
                return newMap;
              });
              const newState = handleItemToggle(item.id, item.type);
              setAllSelectedItems(newState);
            } else if (selectedParentIds.length > 0) {
              // Set the selected parent relationships
              setSelectedParentChildRelationships(prev => {
                const newMap = { ...prev };
                newMap[item.id] = selectedParentIds;
                return newMap;
              });
              // Add to selected items if not already selected
              if (!isAlreadySelected) {
                const newState = handleItemToggle(item.id, item.type);
                setAllSelectedItems(newState);
              }
            }
          }
        });
        return;
      } else if (availableParents.length === 1) {
        // Only one parent available, automatically set the relationship
        const isAlreadySelected = allSelectedItems.Skill.includes(item.id);
        if (isAlreadySelected) {
          // Deselect
          setSelectedParentChildRelationships(prev => {
            const newMap = { ...prev };
            newMap[item.id] = newMap[item.id].filter(id => id !== availableParents[0].id);
            if (newMap[item.id].length === 0) {
              delete newMap[item.id];
            }
            return newMap;
          });
          const newState = handleItemToggle(item.id, item.type);
          setAllSelectedItems(newState);
        } else {
          // Select
          setSelectedParentChildRelationships(prev => {
            const newMap = { ...prev };
            newMap[item.id] = [availableParents[0].id];
            return newMap;
          });
          const newState = handleItemToggle(item.id, item.type);
          setAllSelectedItems(newState);
        }
      }
    }
    
    // Handle skills with single parent or no parents
    if (item.type === 'Skill' && (!item.parents || item.parents.length <= 1)) {
      const isAlreadySelected = allSelectedItems.Skill.includes(item.id);
      
      if (isAlreadySelected) {
        // Deselect
        if (item.parents && item.parents.length === 1) {
          setSelectedParentChildRelationships(prev => {
            const newMap = { ...prev };
            newMap[item.id] = newMap[item.id].filter(id => id !== item.parents[0]);
            if (newMap[item.id].length === 0) {
              delete newMap[item.id];
            }
            return newMap;
          });
        }
        const newState = handleItemToggle(item.id, item.type);
        setAllSelectedItems(newState);
              } else {
          // Select
          if (item.parents && item.parents.length === 1) {
            console.log('Setting parent-child relationship for skill with single parent:', item.id, '->', item.parents[0]);
            setSelectedParentChildRelationships(prev => {
              const newMap = { ...prev };
              newMap[item.id] = [item.parents[0]];
              return newMap;
            });
          }
          const newState = handleItemToggle(item.id, item.type);
          setAllSelectedItems(newState);
        }
      return;
    }
    
    // For other item types - proceed normally
    const newState = handleItemToggle(item.id, item.type);
    setAllSelectedItems(newState);
  };

  const handleStandardToggle = (standardId) => {
    setExpandedStandards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(standardId)) {
        newSet.delete(standardId);
      } else {
        newSet.add(standardId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    const selectedIds = Object.values(allSelectedItems).flat();
    const safeItems = Array.isArray(taxonomyItems) ? taxonomyItems : [];
    const selectedFullItems = safeItems.filter(item => selectedIds.includes(item.id));
    
    // Create the taxonomy array with proper parent_id assignments
    const taxonomyArray = [];
    
    selectedFullItems.forEach(item => {
      if (item.type === 'Skill' && selectedParentChildRelationships[item.id]) {
        // For skills with multiple parents, create separate taxonomy objects for each parent
        selectedParentChildRelationships[item.id].forEach(parentId => {
          taxonomyArray.push({
            taxonomy_id: item.id,
            parent_id: parentId,
            concentration: concentrationId
          });
        });
      } else {
        // For other items or skills with single/no parent
        let parentId = null;
        
        // For skills, determine the parent_id
        if (item.type === 'Skill') {
          // First check if there's a stored parent-child relationship
          if (selectedParentChildRelationships[item.id] && selectedParentChildRelationships[item.id].length > 0) {
            parentId = selectedParentChildRelationships[item.id][0];
          } else if (item.parents && item.parents.length === 1) {
            // If the skill has exactly one parent, use that parent
            parentId = item.parents[0];
          } else if (item.parents && item.parents.length > 1) {
            // If the skill has multiple parents but no relationship is stored, 
            // find the parent that belongs to this concentration
            const availableParent = item.parents.find(parentId => {
              const parent = taxonomyItems.find(p => p.id === parentId);
              return parent && parent.concentrations && parent.concentrations.includes(concentrationId);
            });
            parentId = availableParent || null;
          }
        }
        
        taxonomyArray.push({
          taxonomy_id: item.id,
          parent_id: parentId,
          concentration: concentrationId
        });
      }
    });
    
    console.log('handleSave - selectedParentChildRelationships:', selectedParentChildRelationships);
    console.log('handleSave - taxonomyArray:', taxonomyArray);
    onSelectionChange(selectedIds, selectedFullItems, taxonomyArray);
    onClose();
  };

  // Get standards for this concentration
  const standards = Array.isArray(taxonomyItems)
    ? taxonomyItems.filter(item =>
        item.type === 'Standard' &&
        (item.concentrations && item.concentrations.includes(concentrationId)) &&
        (item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  // Get skills for this concentration
  const skills = Array.isArray(taxonomyItems)
    ? taxonomyItems.filter(item =>
        item.type === 'Skill' &&
        (item.concentrations && item.concentrations.includes(concentrationId)) &&
        (item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  // Get objectives for this concentration
  const objectives = Array.isArray(taxonomyItems)
    ? taxonomyItems.filter(item =>
        item.type === 'Objective' &&
        (item.concentrations && item.concentrations.includes(concentrationId)) &&
        (item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  // Debug logging with more detail about concentration matching
  console.log('Modal debug:', {
    taxonomyItemsLength: taxonomyItems.length,
    concentrationId,
    concentrationIdType: typeof concentrationId,
    standardsLength: standards.length,
    skillsLength: skills.length,
    objectivesLength: objectives.length,
    searchTerm,
    loading,
    sampleTaxonomyItem: taxonomyItems[0],
    sampleConcentrations: taxonomyItems[0]?.concentrations,
    sampleConcentrationTypes: taxonomyItems[0]?.concentrations?.map(c => typeof c),
    sampleTypes: taxonomyItems.slice(0, 5).map(item => ({ 
      id: item.id, 
      type: item.type, 
      concentrations: item.concentrations,
      concentrationTypes: item.concentrations?.map(c => typeof c)
    })),
    // Check if any items have the exact concentration ID
    itemsWithConcentration: taxonomyItems.filter(item => 
      item.concentrations && item.concentrations.includes(concentrationId)
    ).length,
    // Show first few items with their concentration IDs
    firstFewItems: taxonomyItems.slice(0, 3).map(item => ({
      id: item.id,
      type: item.type,
      concentrations: item.concentrations,
      hasConcentration: item.concentrations && item.concentrations.includes(concentrationId)
    }))
  });

  // Use only concentration-specific items - no fallback to all items
  const displayStandards = standards;
  const displaySkills = skills;
  const displayObjectives = objectives;

  // Get skills that belong to a specific standard
  const getSkillsForStandard = (standardId) => {
    return displaySkills.filter(skill => {
      // Check if the skill has this standard as a parent
      const hasParent = skill.parents && skill.parents.includes(standardId);
      if (!hasParent) return false;
      
      // Return all skills that belong to this standard (not just selected ones)
      return true;
    });
  };

  // Get selected skills that belong to a specific standard
  const getSelectedSkillsForStandard = (standardId) => {
    return displaySkills.filter(skill => {
      // Check if the skill has this standard as a parent
      const hasParent = skill.parents && skill.parents.includes(standardId);
      if (!hasParent) return false;
      
      // Check if the skill is selected
      const isSelected = allSelectedItems.Skill.includes(skill.id);
      if (!isSelected) return false;
      
      // Check if this skill is specifically tied to this standard (not another parent)
      const selectedParent = selectedParentChildRelationships[skill.id];
      return selectedParent && selectedParent.includes(standardId);
    });
  };

  const localSelectedItems = allSelectedItems[taxonomyType] || [];

  // Calculate the actual number of taxonomy objects that will be created
  const calculateTaxonomyObjectCount = () => {
    const selectedIds = Object.values(allSelectedItems).flat();
    const safeItems = Array.isArray(taxonomyItems) ? taxonomyItems : [];
    const selectedFullItems = safeItems.filter(item => selectedIds.includes(item.id));
    
    let count = 0;
    
    selectedFullItems.forEach(item => {
      if (item.type === 'Skill' && selectedParentChildRelationships[item.id]) {
        // For skills with multiple parents, count each parent as a separate object
        count += selectedParentChildRelationships[item.id].length;
      } else {
        // For other items or skills with single/no parent, count as 1
        count += 1;
      }
    });
    
    console.log('calculateTaxonomyObjectCount - count:', count, 'selectedItems:', selectedIds.length);
    return count;
  };

  if (!isOpen) return null;

  return (
    <div style={TX.overlay(1000)}>
      <div
        style={{
          ...TX.panel,
          padding: '18px 20px',
          maxWidth: '720px',
          width: 'min(92vw, 720px)',
          maxHeight: '82vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ position: 'relative', marginBottom: '12px', paddingRight: '28px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              background: 'none',
              border: 'none',
              fontSize: '22px',
              lineHeight: 1,
              cursor: 'pointer',
              color: '#94a3b8',
              padding: '2px 4px',
              borderRadius: '6px',
            }}
            aria-label="Close"
          >
            ×
          </button>
          <h2 style={{ ...TX.title, textAlign: 'left', fontSize: '15px' }}>{title}</h2>
          <div style={{ ...TX.hint, marginTop: '6px', textAlign: 'left' }}>
            Only Taxonomy related to <strong>{pathwayDisplayName}</strong> will display.
          </div>
        </div>

        <div
          style={{
            marginBottom: '10px',
            padding: '8px 10px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #f1f5f9',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {[
                { key: 'Standard', label: 'Standards' },
                { key: 'Skill', label: 'Skills' },
                { key: 'Objective', label: 'Objectives' },
              ].map(({ key, label }) => {
                const active = taxonomyType === key;
                const a = TAX_TYPE_ACCENTS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTaxonomyType(key)}
                    aria-pressed={active}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 10px',
                      borderRadius: '8px',
                      border: `1px solid ${active ? a.selBorder : '#e2e8f0'}`,
                      backgroundColor: active ? a.selBg : '#ffffff',
                      color: a.header,
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '2px',
                        backgroundColor: a.stroke,
                      }}
                    />
                    {label}
                  </button>
                );
              })}
            </div>
            {!isStudentRole && (
              <button
                type="button"
                aria-label="Create new taxonomy item"
                title="Create new"
                onClick={() =>
                  onOpenCreateModal(concentrationId, (newItem) => {
                    onFetchTaxonomyItems();
                    console.log('New item created:', newItem);
                  })
                }
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  fontSize: '22px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  flexShrink: 0,
                  marginLeft: 'auto',
                }}
              >
                +
              </button>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={TX.label}>Search</label>
          <input
            type="text"
            placeholder={`Search ${taxonomyType === 'Standard' ? 'standards' : taxonomyType === 'Skill' ? 'skills' : 'objectives'}…`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={TX.field}
          />
        </div>

        {/* Items List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '6px',
            backgroundColor: '#fafafa',
          }}
        >
          {loading ? (
                <div style={{ textAlign: 'center', padding: '16px', color: '#64748b', fontSize: '13px' }}>
                  Loading items…
                </div>
          ) : (
            <>
              {/* Show items based on selected taxonomy type */}
              {taxonomyType === 'Standard' && displayStandards.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: TAX_TYPE_ACCENTS.Standard.header, fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Standards</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {displayStandards.map((standard) => {
                      const standardSkills = getSkillsForStandard(standard.id);
                      const selectedSkillsForStandard = getSelectedSkillsForStandard(standard.id);
                      const isExpanded = expandedStandards.has(standard.id);
                      const isStandardSelected = allSelectedItems.Standard.includes(standard.id);
                      
                      return (
                        <div key={standard.id} style={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              padding: '8px 10px',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              ...typeRowStyle('Standard', isStandardSelected),
                              ...(standardSkills.length > 0 ? { borderBottom: '1px solid #e2e8f0', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}),
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isStandardSelected}
                              onChange={() => handleItemClick(standard)}
                              style={{ marginTop: '2px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, marginBottom: '4px', color: TAX_TYPE_ACCENTS.Standard.header, fontSize: '13px' }}>
                                {standard.title || (
                                  standard.description
                                    ? (standard.description.length > 50
                                        ? `${standard.description.substring(0, 50)}...`
                                        : standard.description)
                                    : `Standard ${standard.id.substring(0, 8)}`
                                )}
                              </div>
                              {standard.description && (
                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                                  {standard.description}
                                </div>
                              )}
                            </div>
                            {standardSkills.length > 0 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStandardToggle(standard.id);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  color: '#666',
                                  padding: '4px'
                                }}
                              >
                                {isExpanded ? '▼' : '▶'}
                              </button>
                            )}
                          </label>
                          
                          {/* Nested Skills */}
                          {isExpanded && standardSkills.length > 0 && (
                            <div style={{ paddingLeft: '20px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                              {standardSkills.map((skill) => {
                                // Check if the skill is selected AND specifically tied to this standard
                                const isSkillSelected = allSelectedItems.Skill.includes(skill.id) && 
                                  selectedParentChildRelationships[skill.id] && selectedParentChildRelationships[skill.id].includes(standard.id);
                                return (
                                  <label
                                    key={skill.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '8px',
                                      padding: '8px 10px',
                                      cursor: 'pointer',
                                      ...typeRowStyle('Skill', isSkillSelected),
                                      borderBottom: '1px solid #e2e8f0',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSkillSelected}
                                      onChange={() => handleItemClick(skill, standard.id)}
                                      style={{ marginTop: '2px' }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 600, marginBottom: '4px', color: TAX_TYPE_ACCENTS.Skill.header, fontSize: '13px' }}>
                                        {skill.title || (
                                          skill.description
                                            ? (skill.description.length > 50
                                                ? `${skill.description.substring(0, 50)}...`
                                                : skill.description)
                                            : `Skill ${skill.id.substring(0, 8)}`
                                        )}
                                      </div>
                                      {skill.description && (
                                        <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                                          {skill.description}
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {taxonomyType === 'Skill' && (
                <>
                  {/* Skills tied to standards */}
                  {displaySkills.filter(skill => skill.parents && skill.parents.length > 0).length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <h3 style={{ margin: '0 0 8px 0', color: TAX_TYPE_ACCENTS.Skill.header, fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Skills (tied to standards)</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {displaySkills
                          .filter(skill => skill.parents && skill.parents.length > 0)
                          .map((skill) => {
                            const isSkillSelected = allSelectedItems.Skill.includes(skill.id);
                            return (
                              <label
                                key={skill.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '8px',
                                  padding: '8px 10px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  ...typeRowStyle('Skill', isSkillSelected),
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSkillSelected}
                                  onChange={() => handleItemClick(skill)}
                                  style={{ marginTop: '2px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, marginBottom: '4px', color: TAX_TYPE_ACCENTS.Skill.header, fontSize: '13px' }}>
                                    {skill.title || (
                                      skill.description
                                        ? (skill.description.length > 50
                                            ? `${skill.description.substring(0, 50)}...`
                                            : skill.description)
                                        : `Skill ${skill.id.substring(0, 8)}`
                                    )}
                                  </div>
                                  {skill.description && (
                                    <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                                      {skill.description}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Standalone Skills */}
                  {displaySkills.filter(skill => !skill.parents || skill.parents.length === 0).length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <h3 style={{ margin: '0 0 8px 0', color: TAX_TYPE_ACCENTS.Skill.header, fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Standalone skills</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {displaySkills
                          .filter(skill => !skill.parents || skill.parents.length === 0)
                          .map((skill) => {
                            const isSkillSelected = allSelectedItems.Skill.includes(skill.id);
                            return (
                              <label
                                key={skill.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '8px',
                                  padding: '8px 10px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  ...typeRowStyle('Skill', isSkillSelected),
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSkillSelected}
                                  onChange={() => handleItemClick(skill)}
                                  style={{ marginTop: '2px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, marginBottom: '4px', color: TAX_TYPE_ACCENTS.Skill.header, fontSize: '13px' }}>
                                    {skill.title || (
                                      skill.description
                                        ? (skill.description.length > 50
                                            ? `${skill.description.substring(0, 50)}...`
                                            : skill.description)
                                        : `Skill ${skill.id.substring(0, 8)}`
                                    )}
                                  </div>
                                  {skill.description && (
                                    <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                                      {skill.description}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {taxonomyType === 'Objective' && displayObjectives.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: TAX_TYPE_ACCENTS.Objective.header, fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Objectives</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {displayObjectives.map((objective) => {
                      const isObjectiveSelected = allSelectedItems.Objective.includes(objective.id);
                      return (
                        <label
                          key={objective.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            ...typeRowStyle('Objective', isObjectiveSelected),
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isObjectiveSelected}
                            onChange={() => handleItemClick(objective)}
                            style={{ marginTop: '2px' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px', color: TAX_TYPE_ACCENTS.Objective.header, fontSize: '13px' }}>
                              {objective.title || (
                                objective.description
                                  ? (objective.description.length > 50
                                      ? `${objective.description.substring(0, 50)}...`
                                      : objective.description)
                                  : `Objective ${objective.id.substring(0, 8)}`
                              )}
                            </div>
                            {objective.description && (
                              <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                                {objective.description}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No items found message */}
              {((taxonomyType === 'Standard' && displayStandards.length === 0) ||
                (taxonomyType === 'Skill' && displaySkills.length === 0) ||
                (taxonomyType === 'Objective' && displayObjectives.length === 0)) && (
                <div style={{ textAlign: 'center', padding: '16px', color: '#64748b', fontSize: '13px' }}>
                  No {taxonomyType.toLowerCase()} found
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            ...TX.foot,
            marginTop: '12px',
          }}
        >
          <button type="button" onClick={onClose} style={TX.btnSecondary}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} style={TX.btnPrimary}>
            Save ({calculateTaxonomyObjectCount()} selected)
          </button>
        </div>
      </div>
    </div>
  );
};

// Parent Selection Modal Component
const ParentSelectionModal = ({ 
  isOpen, 
  onClose, 
  item, 
  parents, 
  onParentSelected,
  concentrationId,
  selectedTaxonomy
}) => {
  const [selectedParents, setSelectedParents] = useState([]);

  // Initialize selected parents when modal opens
  useEffect(() => {
    if (isOpen && item && selectedTaxonomy && concentrationId) {
      // Find already selected parents for this item in the current concentration
      const existingParents = selectedTaxonomy
        .filter(taxonomyItem => 
          taxonomyItem.taxonomy_id === item.id && 
          taxonomyItem.concentration === concentrationId &&
          taxonomyItem.parent_id
        )
        .map(taxonomyItem => taxonomyItem.parent_id);
      
      console.log('ParentSelectionModal - found existing parents for item:', item.id, 'parents:', existingParents);
      setSelectedParents(existingParents);
    } else if (isOpen) {
      setSelectedParents([]);
    }
  }, [isOpen, item, selectedTaxonomy, concentrationId]);

  if (!isOpen || !item) return null;

  const handleParentToggle = (parentId) => {
    setSelectedParents(prev => {
      if (prev.includes(parentId)) {
        return prev.filter(id => id !== parentId);
      } else {
        return [...prev, parentId];
      }
    });
  };

  const handleConfirm = () => {
    console.log('ParentSelectionModal - confirming selected parents:', selectedParents);
    onParentSelected(selectedParents);
    onClose();
  };

  return (
    <div style={TX.overlay(1001)}>
      <div
        style={{
          ...TX.panel,
          padding: '18px 20px',
          maxWidth: '480px',
          width: 'min(92vw, 480px)',
          maxHeight: '82vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <h2 style={{ ...TX.title, fontSize: '15px' }}>Select parents</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              lineHeight: 1,
              cursor: 'pointer',
              color: '#94a3b8',
              padding: '2px 4px',
              borderRadius: '6px',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <p style={{ margin: '0 0 10px 0', color: '#475569', fontSize: '13px', lineHeight: 1.5 }}>
            The <strong>{item.type}</strong> “{item.title || item.description || `Item ${item.id.substring(0, 8)}`}” has
            multiple possible parents. Choose one or more.
          </p>
          <p style={{ margin: 0, color: '#64748b', fontSize: '12px', lineHeight: 1.45 }}>
            Each selection creates a separate link.{' '}
            {selectedParents.length > 0 && (
              <span style={{ color: '#334155', fontWeight: 500 }}>
                {selectedParents.length} selected
              </span>
            )}
          </p>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '6px',
            backgroundColor: '#fafafa',
            minHeight: '120px',
          }}
        >
          {parents.map((parent) => {
            const isSelected = selectedParents.includes(parent.id);
            return (
              <label
                key={parent.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px',
                  marginBottom: '6px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  boxSizing: 'border-box',
                  ...TX.sel(isSelected),
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleParentToggle(parent.id)}
                  style={{ marginTop: '3px', width: '15px', height: '15px', accentColor: '#475569' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: '2px', color: '#0f172a', fontSize: '13px' }}>
                    {parent.title ||
                      (parent.description
                        ? parent.description.length > 50
                          ? `${parent.description.substring(0, 50)}...`
                          : parent.description
                        : `${parent.type} ${parent.id.substring(0, 8)}`)}
                  </div>
                  {parent.description && (
                    <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                      {parent.description}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', fontWeight: 500 }}>
                    {parent.type}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            ...TX.foot,
            marginTop: '14px',
          }}
        >
          <div style={{ fontSize: '12px', color: '#64748b' }}>{selectedParents.length} parent(s)</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={onClose} style={TX.btnSecondary}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedParents.length === 0}
              style={{
                ...TX.btnPrimary,
                ...(selectedParents.length === 0 ? TX.btnPrimaryDisabled : {}),
              }}
            >
              Confirm ({selectedParents.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Taxonomy Modal Component
const CreateTaxonomyModal = ({ 
  isOpen, 
  onClose, 
  concentrationId,
  onItemCreated,
  concentrations,
  taxonomyItems = []
}) => {
  const [createStep, setCreateStep] = useState(1);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    type: '',
    concentration: concentrationId || '',
    parent: ''
  });
  const [parentSearchTerm, setParentSearchTerm] = useState('');



  // Set concentration when modal opens
  useEffect(() => {
    if (isOpen && concentrationId) {
      setNewItem(prev => ({
        ...prev,
        concentration: concentrationId
      }));
    }
  }, [isOpen, concentrationId]);



  const getTypeBadgeStyle = (type) => {
    const a = TAX_TYPE_ACCENTS[type] || TAX_TYPE_ACCENTS.Standard;
    return {
      color: a.header,
      backgroundColor: a.selBg,
      border: `1px solid ${a.selBorder}`,
    };
  };

  const getApplicableParents = (itemType, searchTerm = '') => {
    if (!itemType) return [];
    
    return taxonomyItems.filter(item => {
      // Filter by search term
      const matchesSearch = !searchTerm || 
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Only show items that don't have parents (top-level items)
      if (item.parents && item.parents.length > 0) {
        return false;
      }
      
      // Filter by type compatibility
      if (itemType === 'Standard') {
        return item.type === 'Standard';
      } else if (itemType === 'Skill') {
        return item.type === 'Standard' || item.type === 'Objective';
      } else if (itemType === 'Objective') {
        return item.type === 'Standard' || item.type === 'Objective';
      }
      
      return false;
    });
  };

  const handleCreateItem = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Prepare the data to send
      const taxonomyData = {
        title: newItem.title,
        description: newItem.description,
        type: newItem.type,
        concentrations: newItem.concentration ? [newItem.concentration] : [],
        parents: newItem.parent === '' ? [] : [newItem.parent]
      };
      
      console.log('Sending taxonomy data:', taxonomyData);
      console.log('newItem state:', newItem);
      console.log('concentrationId prop:', concentrationId);
      
      const response = await axios.post('/api/taxonomy', taxonomyData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Created new taxonomy item:', response.data);
      
      if (onItemCreated) {
        onItemCreated(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating taxonomy item:', error);
      console.error('Error response:', error.response?.data);
      alert('Failed to create taxonomy item. Please try again.');
    }
  };

  const resetForm = () => {
    setNewItem({
      title: '',
      description: '',
      type: '',
      concentration: concentrationId || '',
      parent: ''
    });
    setCreateStep(1);
    setParentSearchTerm('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={TX.overlay(1002)}>
      <div
        style={{
          ...TX.panel,
          padding: '18px 20px',
          maxWidth: '520px',
          width: 'min(92vw, 520px)',
          maxHeight: '82vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '12px' }}>
          <div>
            <h2 style={{ ...TX.title, fontSize: '15px' }}>New taxonomy item</h2>
            <div style={{ ...TX.hint, marginTop: '4px' }}>
              {createStep === 2
                ? 'Step 2 of 2 — optional parent'
                : newItem.type === 'Standard'
                  ? 'Top-level standard for this pathway.'
                  : newItem.type
                    ? 'Step 1 of 2 — next you can pick an optional parent.'
                    : 'Choose Standard, Skill, or Objective (same as the selection popup), then add details.'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              lineHeight: 1,
              cursor: 'pointer',
              color: '#94a3b8',
              padding: '2px 4px',
              borderRadius: '6px',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Step 1: Type (legend) + title/description — no separate type step */}
        {createStep === 1 && (
          <div>
            <div
              style={{
                marginBottom: '14px',
                padding: '8px 10px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #f1f5f9',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                {[
                  { key: 'Standard', label: 'Standards' },
                  { key: 'Skill', label: 'Skills' },
                  { key: 'Objective', label: 'Objectives' },
                ].map(({ key, label }) => {
                  const active = newItem.type === key;
                  const a = TAX_TYPE_ACCENTS[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewItem({ ...newItem, type: key, parent: '' })}
                      aria-pressed={active}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: `1px solid ${active ? a.selBorder : '#e2e8f0'}`,
                        backgroundColor: active ? a.selBg : '#ffffff',
                        color: a.header,
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '2px',
                          backgroundColor: a.stroke,
                        }}
                      />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                marginBottom: '14px',
                padding: '10px 12px',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                color: '#475569',
                fontSize: '13px',
              }}
            >
              {(() => {
                const pathway = concentrations.find(c => c.id === concentrationId);
                return (
                  <>
                    <span style={{ fontWeight: 600, color: '#334155' }}>Pathway</span>
                    <div style={{ marginTop: '4px' }}>{pathway ? pathway.title : concentrationId}</div>
                    {pathway && pathway.description && (
                      <div style={{ fontSize: '12px', marginTop: '6px', color: '#64748b', lineHeight: 1.45 }}>
                        {pathway.description}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={TX.label}>
                Title <span style={{ color: '#94a3b8' }}>*</span>
              </label>
              <input
                type="text"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="Title"
                style={TX.field}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={TX.label}>Description</label>
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Optional description"
                rows={3}
                style={{ ...TX.field, resize: 'vertical', minHeight: '72px' }}
              />
            </div>

            {newItem.type === 'Standard' && (
              <div
                style={{
                  marginBottom: '12px',
                  padding: '10px 12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  fontSize: '12px',
                  lineHeight: 1.45,
                }}
              >
                Standards are top-level only — parent step is skipped.
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '16px' }}>
              <button type="button" onClick={handleClose} style={TX.btnSecondary}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => (newItem.type === 'Standard' ? handleCreateItem() : setCreateStep(2))}
                disabled={!newItem.type || !newItem.title.trim()}
                style={{
                  ...TX.btnPrimary,
                  ...(!newItem.type || !newItem.title.trim() ? TX.btnPrimaryDisabled : {}),
                }}
              >
                {newItem.type === 'Standard' ? 'Create' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Parent Selection (Skill / Objective only) */}
        {createStep === 2 && (newItem.type === 'Skill' || newItem.type === 'Objective') && (
          <div>
            <div
              style={{
                marginBottom: '12px',
                padding: '10px 12px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #f1f5f9',
                fontSize: '13px',
                color: '#475569',
                lineHeight: 1.5,
              }}
            >
              <div>
                <span style={{ color: '#64748b', fontWeight: 500 }}>Type</span> {newItem.type}
              </div>
              <div style={{ marginTop: '4px' }}>
                <span style={{ color: '#64748b', fontWeight: 500 }}>Title</span> {newItem.title}
              </div>
              <div style={{ marginTop: '4px' }}>
                <span style={{ color: '#64748b', fontWeight: 500 }}>Pathway</span>{' '}
                {(() => {
                  const pathway = concentrations.find(c => c.id === concentrationId);
                  return pathway ? pathway.title : concentrationId;
                })()}
              </div>
            </div>

            {newItem.type !== 'Standard' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={TX.label}>Parent (optional)</label>
                <input
                  type="text"
                  value={parentSearchTerm}
                  onChange={(e) => setParentSearchTerm(e.target.value)}
                  placeholder="Search parents…"
                  style={{ ...TX.field, marginBottom: '10px' }}
                />

                <div
                  style={{
                    maxHeight: '280px',
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '6px',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <label style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="radio"
                      name="parent"
                      checked={newItem.parent === ''}
                      onChange={() => setNewItem({ ...newItem, parent: '' })}
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ color: '#64748b', fontSize: '13px' }}>No parent (top level)</span>
                  </label>
                  {getApplicableParents(newItem.type, parentSearchTerm).map(parent => (
                    <label
                      key={parent.id}
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        position: 'relative',
                        cursor: 'pointer',
                        padding: '8px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        backgroundColor: newItem.parent === parent.id ? '#f1f5f9' : '#fff',
                      }}
                      title={`${parent.title || 'Untitled'}\n\n${parent.description || 'No description available'}`}
                    >
                      <input
                        type="radio"
                        name="parent"
                        checked={newItem.parent === parent.id}
                        onChange={() => setNewItem({...newItem, parent: parent.id})}
                        style={{ marginRight: '8px', float: 'left', marginTop: '2px' }}
                      />
                      <div style={{ marginLeft: '24px' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '4px'
                        }}>
                          <span
                            style={{
                              ...getTypeBadgeStyle(parent.type),
                              fontWeight: 600,
                              marginRight: '8px',
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              display: 'inline-block',
                            }}
                          >
                            {parent.type}:
                          </span>
                          <span style={{ 
                            fontWeight: '600',
                            fontSize: '14px'
                          }}>
                            {parent.title || `Item ${parent.id.substring(0, 8)}`}
                          </span>
                        </div>
                        {parent.description && (
                          <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45, marginTop: '4px' }}>
                            {parent.description}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '16px' }}>
              <button type="button" onClick={() => setCreateStep(1)} style={TX.btnSecondary}>
                Back
              </button>
              <button type="button" onClick={handleCreateItem} style={TX.btnPrimary}>
                Create
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxonomySelector; 
